import LocalStorageManager from '@/managers/local-storage-manager';
import { Appearance, AppearanceType, THEME_ATTRIBUTE } from '@/constants';
import { LocalStorageKeys } from '@/constants/local-storage-keys';

const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

/**
 * Owns the resolved appearance: the `data-theme` attribute on <html>, the persisted
 * preference and the system-preference listener.
 *
 * There is no "automatic" mode the user can pick. Until they choose explicitly the app
 * follows the device, and from then on their choice wins on every device this browser
 * profile is used on.
 */
export class AppearanceManager {
	private static current: AppearanceType = Appearance.LIGHT;
	private static listeners = new Set<() => void>();
	private static mediaQuery: MediaQueryList | null = null;

	static init(): void {
		this.current = this.getStoredAppearance() ?? this.getSystemAppearance();
		this.applyToDocument(this.current);
		this.watchSystemAppearance();
	}

	static getAppearance(): AppearanceType {
		return this.current;
	}

	static isDark(): boolean {
		return this.current === Appearance.DARK;
	}

	static setAppearance(theme: AppearanceType): void {
		LocalStorageManager.setLocalStorage(LocalStorageKeys.APPEARANCE, theme);
		this.update(theme);
	}

	static toggle(): void {
		this.setAppearance(this.isDark() ? Appearance.LIGHT : Appearance.DARK);
	}

	/** Drops the explicit choice and hands control back to the device preference. */
	static clearAppearance(): void {
		LocalStorageManager.removeLocalStorage(LocalStorageKeys.APPEARANCE);
		this.update(this.getSystemAppearance());
	}

	static subscribe(listener: () => void): () => void {
		this.listeners.add(listener);
		return () => this.listeners.delete(listener);
	}

	private static getStoredAppearance(): AppearanceType | null {
		const stored = LocalStorageManager.getLocalStorage<AppearanceType>(LocalStorageKeys.APPEARANCE);

		return stored === Appearance.DARK || stored === Appearance.LIGHT ? stored : null;
	}

	private static getSystemAppearance(): AppearanceType {
		return window.matchMedia?.(DARK_MEDIA_QUERY).matches ? Appearance.DARK : Appearance.LIGHT;
	}

	private static applyToDocument(theme: AppearanceType): void {
		document.documentElement.setAttribute(THEME_ATTRIBUTE, theme);
	}

	private static update(theme: AppearanceType): void {
		if (this.current === theme) {
			return;
		}

		this.current = theme;
		this.applyToDocument(theme);
		this.listeners.forEach(listener => listener());
	}

	private static watchSystemAppearance(): void {
		if (this.mediaQuery || !window.matchMedia) {
			return;
		}

		this.mediaQuery = window.matchMedia(DARK_MEDIA_QUERY);
		this.mediaQuery.addEventListener('change', event => {
			// An explicit choice outranks the device.
			if (this.getStoredAppearance()) {
				return;
			}

			this.update(event.matches ? Appearance.DARK : Appearance.LIGHT);
		});
	}
}
